'use client';

import { useEffect, useState } from 'react';
import { HelpCircle, Loader2, MessageCircleQuestion } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from '@/components/ui/accordion';
import FadeIn from './Framer/FadeIn';
import { useTranslations } from 'next-intl';

type Faq = {
    id: number | string;
    question: string;
    answer: string;
};

export default function FaqPageClient() {
    const t = useTranslations('Faq');
    const [shortFaqs, setShortFaqs] = useState<Faq[]>([]);
    const [longFaqs, setLongFaqs] = useState<Faq[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetch('/api/public/faqs', { cache: 'no-store' })
            .then((res) => res.json())
            .then((data) => {
                setShortFaqs(data?.short || []);
                setLongFaqs(data?.long || []);
            })
            .catch(() => {
                setShortFaqs([]);
                setLongFaqs([]);
            })
            .finally(() => setLoading(false));
    }, []);

    return (
        <FadeIn>
            <div className="max-w-4xl mx-auto px-4 pt-32 pb-24">
                <div className="text-center mb-12">
                    <div className="inline-flex items-center justify-center h-12 w-12 rounded-full bg-primary/10 mb-4">
                        <HelpCircle className="h-6 w-6 text-primary" />
                    </div>
                    <h1 className="text-4xl font-bold text-foreground mb-3">{t('title')}</h1>
                    <p className="text-muted-foreground max-w-xl mx-auto">
                        {t('subtitle')}
                    </p>
                </div>

                {loading ? (
                    <div className="flex justify-center py-16">
                        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                    </div>
                ) : (
                    <div className="space-y-16">
                        {shortFaqs.length > 0 && (
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 items-start">
                                {shortFaqs.map((faq) => (
                                    <Card key={faq.id} className="overflow-hidden self-start">
                                        <Accordion type="single" collapsible>
                                            <AccordionItem value={String(faq.id)} className="border-b-0">
                                                <AccordionTrigger className="px-4 py-3.5 text-base font-semibold hover:no-underline">
                                                    {faq.question}
                                                </AccordionTrigger>
                                                <AccordionContent className="px-4 pb-4 text-muted-foreground">
                                                    {faq.answer}
                                                </AccordionContent>
                                            </AccordionItem>
                                        </Accordion>
                                    </Card>
                                ))}
                            </div>
                        )}

                        {longFaqs.length > 0 && (
                            <div className="divide-y divide-border">
                                {longFaqs.map((faq) => (
                                    <div key={faq.id} className="py-6 first:pt-0 last:pb-0">
                                        <h3 className="font-bold text-lg text-foreground mb-2">{faq.question}</h3>
                                        <p className="text-muted-foreground leading-relaxed whitespace-pre-line">{faq.answer}</p>
                                    </div>
                                ))}
                            </div>
                        )}

                        {shortFaqs.length === 0 && longFaqs.length === 0 && (
                            <div className="text-center py-16">
                                <MessageCircleQuestion className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
                                <p className="text-muted-foreground">{t('empty')}</p>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </FadeIn>
    );
}
